// Front and back body diagrams with a region per muscle group. Pure data:
// the MuscleMap component draws it.
import type { MuscleGroup } from '@squats/shared';

export type Region =
  | { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number; rotate?: number }
  | { kind: 'poly'; points: [number, number][] };

type Part = { group: MuscleGroup | null; region: Region };

export const MAP_VIEW = { width: 260, height: 276 } as const;
const FRONT_X = 66;
const BACK_X = 194;

const e = (cx: number, cy: number, rx: number, ry: number, rotate = 0): Region => ({ kind: 'ellipse', cx, cy, rx, ry, rotate });
const p = (...points: [number, number][]): Region => ({ kind: 'poly', points });

/** Mirror a left-side part (x measured from the centre line) to both sides of a figure centred at cx. */
function both(cx: number, group: MuscleGroup | null, make: (side: 1 | -1, cx: number) => Region): Part[] {
  return [
    { group, region: make(-1, cx) },
    { group, region: make(1, cx) },
  ];
}
const ex = (cx: number, side: 1 | -1, dx: number) => cx + side * dx;

/** Head, neck, hands, knees, feet: shared by both views, never highlighted. */
function frame(cx: number): Part[] {
  return [
    { group: null, region: e(cx, 22, 12, 15) },
    { group: null, region: p([cx - 6, 34], [cx + 6, 34], [cx + 7, 44], [cx - 7, 44]) },
    ...both(cx, null, (s) => e(ex(cx, s, 41), 136, 5.5, 7)), // hands
    ...both(cx, null, (s) => e(ex(cx, s, 12), 196, 6.5, 6)), // knees
    ...both(cx, null, (s) => e(ex(cx, s, 13), 252, 8, 4)), // feet
    { group: null, region: p([cx - 20, 124], [cx + 20, 124], [cx + 23, 140], [cx - 23, 140]) }, // pelvis
  ];
}

const FRONT: Part[] = [
  ...frame(FRONT_X),
  // Lower legs (front): neutral shins.
  ...both(FRONT_X, null, (s) => e(ex(FRONT_X, s, 13), 222, 6.5, 22)),
  ...both(FRONT_X, 'traps', (s, c) => p([ex(c, s, 6), 40], [ex(c, s, 20), 47], [ex(c, s, 6), 47])),
  ...both(FRONT_X, 'shoulders', (s, c) => e(ex(c, s, 27), 56, 10, 11)),
  ...both(FRONT_X, 'chest', (s, c) => p([ex(c, s, 1), 50], [ex(c, s, 18), 49], [ex(c, s, 22), 64], [ex(c, s, 14), 76], [ex(c, s, 1), 76])),
  ...both(FRONT_X, 'biceps', (s, c) => e(ex(c, s, 32), 82, 6.5, 15, -s * 8)),
  ...both(FRONT_X, 'forearms', (s, c) => e(ex(c, s, 38), 112, 6, 17, -s * 8)),
  { group: 'core', region: p([FRONT_X - 12, 79], [FRONT_X + 12, 79], [FRONT_X + 11, 123], [FRONT_X - 11, 123]) },
  ...both(FRONT_X, 'core', (s, c) => p([ex(c, s, 14), 80], [ex(c, s, 21), 80], [ex(c, s, 19), 118], [ex(c, s, 13), 122])),
  ...both(FRONT_X, 'quads', (s, c) => p([ex(c, s, 8), 141], [ex(c, s, 20), 140], [ex(c, s, 21), 160], [ex(c, s, 17), 188], [ex(c, s, 7), 188], [ex(c, s, 7), 172])),
  // Inner thigh.
  ...both(FRONT_X, 'adductors', (s, c) => p([ex(c, s, 2), 142], [ex(c, s, 7), 142], [ex(c, s, 6), 172], [ex(c, s, 3), 162])),
];

const BACK: Part[] = [
  ...frame(BACK_X),
  ...both(BACK_X, 'back', (s, c) => p([ex(c, s, 1), 58], [ex(c, s, 19), 56], [ex(c, s, 23), 72], [ex(c, s, 18), 98], [ex(c, s, 1), 108])),
  { group: 'traps', region: p([BACK_X, 38], [BACK_X - 20, 50], [BACK_X - 8, 60], [BACK_X, 82], [BACK_X + 8, 60], [BACK_X + 20, 50]) },
  ...both(BACK_X, 'shoulders', (s, c) => e(ex(c, s, 27), 56, 10, 11)),
  ...both(BACK_X, 'triceps', (s, c) => e(ex(c, s, 32), 82, 6.5, 15, -s * 8)),
  ...both(BACK_X, 'forearms', (s, c) => e(ex(c, s, 38), 112, 6, 17, -s * 8)),
  { group: 'lower_back', region: p([BACK_X - 12, 106], [BACK_X + 12, 106], [BACK_X + 13, 124], [BACK_X - 13, 124]) },
  ...both(BACK_X, 'glutes', (s, c) => e(ex(c, s, 10), 140, 11, 12)),
  ...both(BACK_X, 'hamstrings', (s, c) => p([ex(c, s, 3), 154], [ex(c, s, 20), 152], [ex(c, s, 18), 188], [ex(c, s, 6), 188])),
  ...both(BACK_X, 'calves', (s, c) => e(ex(c, s, 13), 220, 7.5, 18)),
];

export type MapRegion = { group: MuscleGroup | null; region: Region; level: 'primary' | 'secondary' | 'none' | 'neutral' };

/** Every region with how strongly it's worked. */
export function muscleMap(primary: readonly MuscleGroup[], secondary: readonly MuscleGroup[]): MapRegion[] {
  return [...FRONT, ...BACK].map((part) => ({
    ...part,
    level: part.group === null ? 'neutral' : primary.includes(part.group) ? 'primary' : secondary.includes(part.group) ? 'secondary' : 'none',
  }));
}

export const MAP_LABELS = [
  { x: FRONT_X, text: 'Front' },
  { x: BACK_X, text: 'Back' },
];
